import { request } from '@umijs/max'
import type {
  NotifyMsg,
  NotifySearchParams,
  NotifyPageResponse,
  CreateNotifyParams,
  UpdateNotifyParams,
  NotifyPlatformExt,
  NotifyPushStatus,
  NotifyUserPlatform,
  NotifyUser,
  NotifyMsgAttachment,
  NotifyOperationLog,
  NotifyOperationLogQueryParams,
  NotifyOperationLogPageResponse,
  UserUnreadNotify,
} from './types'

const serviceApi = '/admin-api/system/notify-msg'

// 通知消息相关API
export const notifyService = {
  // 获取通知列表
  getNotifyPage: async (params: NotifySearchParams): Promise<API.Result<NotifyPageResponse>> => {
    return request(`${serviceApi}/page`, {
      method: 'GET',
      params,
    })
  },
  // 获取通知列表
  getNotifyList: async (params: NotifySearchParams): Promise<API.Result<NotifyPageResponse>> => {
    return request(`${serviceApi}/list`, {
      method: 'GET',
      params,
    })
  },

  // 获取通知详情
  getNotifyDetail: async (params: {id: string}): Promise<API.Result<NotifyMsg>> => {
    return request(`${serviceApi}/get`, {
      method: 'GET',
      params,
    })
  },

  // 创建通知并推送
  createNotify: async (data: CreateNotifyParams): Promise<API.Result<NotifyMsg>> => {
    return request(`${serviceApi}/create`, {
      method: 'POST',
      data,
    })
  },
  // 创建通知草稿
  createDraftNotify: async (data: CreateNotifyParams): Promise<API.Result<NotifyMsg>> => {
    return request(`${serviceApi}/draft/create`, {
      method: 'POST',
      data,
    })
  },

  // 更新通知
  updateNotify: async (data: UpdateNotifyParams): Promise<API.Result<NotifyMsg>> => {
    return request(`${serviceApi}/update`, {
      method: 'PUT',
      data,
    })
  },

  // 删除通知
  deleteNotify: async (params: {id: string}): Promise<API.Result<void>> => {
    return request(`${serviceApi}/delete`, {
      method: 'DELETE',
      data: params,
    })
  },

  // 发布通知
  publishNotify: async (params: {id: string}): Promise<API.Result<void>> => {
    return request(`${serviceApi}/publish`, {
      method: 'POST',
      data: params,
    })
  },

  // 撤回通知
  withdrawNotify: async (params: {id: string, withdrawReason?: string}): Promise<API.Result<void>> => {
    return request(`${serviceApi}/withdraw`, {
      method: 'POST',
      data: params,
    })
  },

  // 推送通知到第三方平台
  pushToPlatform: async (params: {id: string, platformType: string}): Promise<API.Result<void>> => {
    return request(`${serviceApi}/push`, {
      method: 'POST',
      data: params,
    })
  },
}

// 平台扩展相关API
export const platformExtService = {

  // 创建平台扩展
  createPlatformExt: async (data: NotifyPlatformExt): Promise<API.Result<NotifyPlatformExt>> => {
    return request(`${serviceApi}/platform-ext`, {
      method: 'POST',
      data,
    })
  },

  // 更新平台扩展
  updatePlatformExt: async (data: NotifyPlatformExt): Promise<API.Result<NotifyPlatformExt>> => {
    return request(`${serviceApi}/platform-ext/${data.id}`, {
      method: 'PUT',
      data,
    })
  },

  // 删除平台扩展
  deletePlatformExt: async (id: string): Promise<API.Result<void>> => {
    return request(`${serviceApi}/platform-ext/${id}`, {
      method: 'DELETE',
    })
  },
}

// 推送状态相关API
export const pushStatusService = {
  // 获取推送状态列表
  getPushStatusList: async (notifyMsgId: string): Promise<API.Result<NotifyPushStatus[]>> => {
    return request(`${serviceApi}/${notifyMsgId}/push-status`, {
      method: 'GET',
    })
  },

  // 重试推送
  retryPush: async (id: string): Promise<API.Result<void>> => {
    return request(`${serviceApi}/push-status/${id}/retry`, {
      method: 'POST',
    })
  },
}

// 用户平台关联相关API
export const userPlatformService = {
  // 获取用户平台关联列表
  getUserPlatformList: async (params: {
    platformType?: string
    current?: number
    pageSize?: number
  }): Promise<API.Result<{ list: NotifyUserPlatform[]; total: number }>> => {
    return request(`${serviceApi}/user-platforms`, {
      method: 'GET',
      params,
    })
  },

  // 同步用户平台关联
  syncUserPlatform: async (platformType: string): Promise<API.Result<void>> => {
    return request(`${serviceApi}/user-platforms/sync`, {
      method: 'POST',
      data: { platformType },
    })
  },
}

// 通知用户相关API
export const notifyUserService = {
  // 获取通知用户列表
  getNotifyUserList: async (notifyMsgId: string, params: {
    readStatus?: number
    current?: number
    pageSize?: number
  }): Promise<API.Result<{ list: NotifyUser[]; total: number }>> => {
    return request(`${serviceApi}/${notifyMsgId}/users`, {
      method: 'GET',
      params,
    })
  },

  // 标记已读
  markAsRead: async (notifyMsgId: string, userId: string): Promise<API.Result<void>> => {
    return request(`${serviceApi}/${notifyMsgId}/users/${userId}/read`, {
      method: 'POST',
    })
  },

  // 获取当前用户未读消息列表
  getUnreadNotifyList: async (params: {
    pageSize?: number
    current?: number
  }): Promise<API.Result<UserUnreadNotify[]>> => {
    return request(`${serviceApi}/my-unread`, {
      method: 'GET',
      params,
    })
  },

  // 获取当前用户所有消息列表（支持搜索和分页）
  getMyNotifyList: async (params: {
    keyword?: string
    messageType?: number
    priority?: number
    readStatus?: boolean
    current?: number
    pageSize?: number
  }): Promise<API.Result<{ list: UserUnreadNotify[]; total: number }>> => {
    return request(`${serviceApi}/my-list`, {
      method: 'GET',
      params,
    })
  },

  // 标记消息为已读（当前用户）
  markNotifyAsRead: async (notifyMsgId: string): Promise<API.Result<void>> => {
    return request(`${serviceApi}/${notifyMsgId}/mark-read`, {
      method: 'POST',
    })
  },

  // 获取未读消息数量
  getUnreadCount: async (): Promise<API.Result<number>> => {
    return request(`${serviceApi}/unread-count`, {
      method: 'GET',
    })
  },

  // 一键标记所有消息为已读
  markAllAsRead: async (): Promise<API.Result<void>> => {
    return request(`${serviceApi}/mark-all-read`, {
      method: 'POST',
    })
  },
}

// 附件相关API
export const attachmentService = {
  // 获取附件列表
  getAttachmentList: async (notifyMsgId: string): Promise<API.Result<NotifyMsgAttachment[]>> => {
    return request(`${serviceApi}/${notifyMsgId}/attachments`, {
      method: 'GET',
    })
  },

  // 上传附件
  uploadAttachment: async (notifyMsgId: string, file: File): Promise<API.Result<NotifyMsgAttachment>> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('notifyMsgId', notifyMsgId.toString())
    
    return request(`${serviceApi}/attachment/upload`, {
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
    },
    })
  },

  // 删除附件
  deleteAttachment: async (id: string): Promise<API.Result<void>> => {
    return request(`${serviceApi}/attachment/${id}`, {
      method: 'DELETE',
    })
  },

  // 下载附件
  downloadAttachment: async (id: string): Promise<API.Result<Blob>> => {
    return request(`${serviceApi}/attachment/${id}/download`, {
      method: 'GET',
      responseType: 'blob',
    })
  },
}

// 操作日志相关API
export const operationLogService = {
  // 获取操作日志分页列表
  getOperationLogPage: async (params: NotifyOperationLogQueryParams): Promise<API.Result<NotifyOperationLogPageResponse>> => {
    return request(`${serviceApi}/operation-log/page`, {
      method: 'POST',
      data: params,
    })
  },
}

